import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

export const Atelier = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section id="atelier" ref={ref} className="px-5 md:px-14 lg:px-20 py-16 md:py-24" data-testid="atelier-section">
      <div className="grid md:grid-cols-12 gap-10 md:gap-8 items-center">
        
        {/* Colonna Sinistra: Immagine / Emblema */}
        <div className="md:col-span-7 relative aspect-[16/11] overflow-hidden bg-[#0d0d0d] flex items-center justify-center p-6 md:p-10 spotlight-frame rounded-sm">
          <motion.img 
            style={{ y }} 
            src="alkeon-logo.jpg" 
            alt="ALKEON Timeless Design Emblem" 
            className="max-h-full max-w-full object-contain rounded-xl filter brightness-90 contrast-105 shadow-[0_20px_50px_rgba(0,0,0,0.9)] transition-transform duration-1000 ease-out hover:scale-[1.03]" 
          />
        </div>

        {/* Colonna Destra: Contenuti */}
        <motion.div
          initial={{ opacity: 0.001, y: 30 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-5 md:col-start-8"
        >
          <p className="label mb-3">III — Atelier</p>
          <h2 className="font-serif text-bone font-light text-4xl md:text-5xl leading-[1.05]">
            Cast slowly,<br /><em>in small numbers.</em>
          </h2>
          <p className="body mt-5 text-sm md:text-base leading-relaxed">
            Every object is formulated, hand-poured, cured and finished within our studio in Recanati, Italy. We work in disciplined, limited series so new forms arrive quietly — and leave just as quietly.
          </p>

          {/* Core Finishes Section - 1 Column on Mobile, 3 on Desktop */}
          <div className="mt-6 border-t border-bone/10 pt-5">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bone/50 mb-4 font-medium">Core Finishes</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-5">
              <div>
                <p className="font-serif text-bone text-lg font-light">01</p>
                <p className="label mt-1 text-[11px] font-medium text-bone uppercase tracking-wider">Moonstone</p>
                <p className="mt-2 text-[11px] italic text-bone/65 leading-relaxed">
                  A cold, pale mineral composite marked by organic charcoal veins and raw surface micro-pores — evoking ancient, moonlit fossils.
                </p>
              </div>
              <div>
                <p className="font-serif text-bone text-lg font-light">02</p>
                <p className="label mt-1 text-[11px] font-medium text-bone uppercase tracking-wider">Ancient Sand</p>
                <p className="mt-2 text-[11px] italic text-bone/65 leading-relaxed">
                  A warm mineral matrix reminiscent of sun-bleached sandstone, rich in organic micro-cavities and time-sculpted erosion patterns.
                </p>
              </div>
              <div>
                <p className="font-serif text-bone text-lg font-light">03</p>
                <p className="label mt-1 text-[11px] font-medium text-bone uppercase tracking-wider">Ash Rock</p>
                <p className="mt-2 text-[11px] italic text-bone/65 leading-relaxed">
                  A dense, dark mineral body with basalt-like micro-cavities, sculpted to absorb light and hold deep shadow within its porous stone surface.
                </p>
              </div>
            </div>
          </div>

          {/* Nota sulla varianza artigianale */}
          <p className="mt-5 text-xs leading-relaxed text-bone/60">
            Because every object is hand-cast, individual pieces may vary slightly in texture and tone. New experimental finishes emerge quietly over time, and custom pigment blends or commissions can be developed upon request.
          </p>

          {/* Direct Atelier Contact Block */}
          <div className="mt-8 border-t border-bone/10 pt-5 flex flex-col gap-1">
            <p className="text-[10px] uppercase tracking-[0.25em] text-bone/50 font-medium">Inquiries & Commissions</p>
            <p className="text-xs text-bone/75 leading-relaxed mt-1">
              For private commissions, tailored finishes, or general assistance:
            </p>
            <a 
              href="mailto:atelier@alkeontimelessdesign.com" 
              className="font-serif italic text-stone hover:text-bone text-base tracking-wide transition-colors mt-1 underline decoration-stone/30 underline-offset-4"
            >
              atelier@alkeontimelessdesign.com
            </a>
          </div>

        </motion.div>

      </div>
    </section>
  );
};